import clsx from "clsx";
import {
  B2BSpinner,
  LoadingIndicator,
  TravelerSelectStep,
  TravelerSelectWorkflow,
  hasInvalidBirthdate,
} from "halifax";
import React, { useMemo, useState } from "react";
import {
  CallState,
  CLICKED_ADD_TRAVELER,
  ClickedAddTravelerProperties,
  ICorpPerson,
  IPerson,
  ITravelerStepErrors,
  PersonId,
  SELECT_TRAVELERS,
} from "redmond";

import { Box, Typography } from "@material-ui/core";
import { getConfigTenant, isCorpTenant, isVesperTenant } from "@capone/common";
import { config } from "../../../../../api/config";
import { trackEvent } from "../../../../../api/v1/analytics/trackEvent";
import {
  addTrackingProperties,
  AVAILABLE,
  CORP_EMBEDDED_TRAVELER_FORM_EXPERIMENT,
  getExperimentVariant,
  useExperiments,
} from "../../../../../context/experiments";
import {
  TRAVELER_SELECT_DROPDOWN_DESCRIPTION,
  TRAVELER_SELECT_DROPDOWN_TITLE,
} from "../DriverSelectDropdownForm/constants";
import { MobileBookDriverSelectionConnectorProps } from "./container";
import "./styles.scss";
import * as constants from "./textConstants";

export interface IMobileBookDriverSelectionProps
  extends MobileBookDriverSelectionConnectorProps {
  progress: TravelerSelectStep;
  setProgress: (progress: TravelerSelectStep) => void;
  onProgressChange?: (step: TravelerSelectStep) => void;
  onContinue?: (travelersChanged: string[]) => void;
  onGoBack?: (travelersChanged: string[]) => void;
  className?: string;
  selectionScreenHeaderElement?: JSX.Element;
  onReviewStep?: boolean;
  disabled?: boolean;
  combinedStep?: boolean;
  showErrors?: ITravelerStepErrors;
  setShowErrors?: (showErrors: ITravelerStepErrors) => void;
  saveButtonClicked?: boolean;
}

export const MobileBookDriverSelection = (
  props: IMobileBookDriverSelectionProps
) => {
  const {
    className,
    travelers,
    progress,
    selectionScreenHeaderElement,
    setProgress,
    onProgressChange,
    onContinue,
    onGoBack,
    setUserSelectedPassengerIds,
    updateUserPassenger,
    deleteUserPassenger,
    userPassengerCallState,
    onReviewStep,
    disabled,
    combinedStep,
    showErrors,
    setShowErrors,
    saveButtonClicked,
    selectedTravelerId,
    fetchCorpUserPassengers,
    isMissingLicense,
    userRoles,
  } = props;
  const isVesper = isVesperTenant(getConfigTenant());

  const [localTravelerIds, setLocalTravelerIds] = useState<string[]>([]);
  const [travelersChanged, setTravelersChanged] = useState<string[]>([]);

  const expState = useExperiments();
  const useEmbeddedTravelerForm =
    getExperimentVariant(
      expState.experiments,
      CORP_EMBEDDED_TRAVELER_FORM_EXPERIMENT
    ) === AVAILABLE;

  React.useEffect(() => {
    fetchCorpUserPassengers();
  }, []);

  const selectedTravelerIds = useMemo(
    () => (selectedTravelerId ? [selectedTravelerId] : []),
    [selectedTravelerId]
  );

  React.useEffect(() => {
    if (
      travelers.length === 1 &&
      !((travelers[0] as ICorpPerson).isMissingInfo ?? false) &&
      !hasInvalidBirthdate(travelers[0].dateOfBirth)
    ) {
      setUserSelectedPassengerIds({
        userSelectedPassengerIds: [travelers[0].id],
      });
    }
  }, [travelers]);

  React.useEffect(() => {
    if (selectedTravelerId && isMissingLicense) {
      setProgress(TravelerSelectStep.TravelerInfoForm);
    }
  }, [selectedTravelerId, isMissingLicense]);

  const handleContinue = () => {
    onContinue && onContinue(travelersChanged);
    setLocalTravelerIds(selectedTravelerIds);
    setTravelersChanged([]);
  };

  const handleGoBack = () => {
    onGoBack && onGoBack(travelersChanged);
    setLocalTravelerIds(selectedTravelerIds);
  };

  return (
    <div>
      {userPassengerCallState === CallState.InProcess ? (
        <LoadingIndicator
          indicatorSize="small"
          indicator={B2BSpinner}
          message={
            userPassengerCallState === CallState.InProcess
              ? constants.UPDATE_TEXT
              : constants.VALIDATE_TEXT
          }
        />
      ) : (
        <TravelerSelectWorkflow
          showDriverLicenseSection
          displayModalSubtitle
          showGenderField
          showNationalityField
          singleTravelerWorkflow
          buttonClassName="b2b"
          className={clsx(
            "mobile-car-book-passenger-selection-root",
            className,
            {
              "combined-step": combinedStep,
              redesigned: isVesper,
            }
          )}
          disabled={disabled}
          errorMessage={constants.ADD_TRAVELER_ERROR_MESSAGE}
          handleDeletePassenger={(travelerId: PersonId) => {
            const newTravelerIdList = localTravelerIds.filter(
              (id) => id !== travelerId
            );
            setLocalTravelerIds(newTravelerIdList);
            deleteUserPassenger({ personId: travelerId });
          }}
          handleUpdatePassenger={(
            traveler: IPerson,
            hideProfileAction,
            _,
            __,
            isNewTraveler
          ) => {
            if (isNewTraveler)
              setLocalTravelerIds([...localTravelerIds, traveler.id]);

            updateUserPassenger({
              person: traveler,
              hideProfileAction,
            });
          }}
          isMobile
          onContinue={handleContinue}
          onGoBack={handleGoBack}
          onProgressChange={onProgressChange}
          progress={progress}
          saveButtonClicked={saveButtonClicked}
          selectedTravelerIds={selectedTravelerIds}
          selectionScreenHeaderElement={selectionScreenHeaderElement}
          setProgress={setProgress}
          setSelectedTravelerIds={(travelerIds: string[]) => {
            trackEvent({
              eventName: SELECT_TRAVELERS,
              properties: {},
            });

            const changedTravelersList = travelerIds.filter(
              (id) => !localTravelerIds.includes(id)
            );

            setTravelersChanged(changedTravelersList);
            setUserSelectedPassengerIds({
              userSelectedPassengerIds: travelerIds,
            });
          }}
          setShowErrors={setShowErrors}
          showErrors={showErrors}
          titles={{
            travelerInfoTitle: onReviewStep
              ? constants.TRAVELER_INFORMATION_TITLE
              : "",
            travelerInfoSubtitle: onReviewStep
              ? TRAVELER_SELECT_DROPDOWN_DESCRIPTION
              : "",
            frequentFlyerTitle: constants.FREQUENT_FLYER_TITLE,
            additionalInfoTitle: constants.ADDITIONAL_INFO_TITLE,
            adultTitle: constants.ADULT_TITLE,
            childTitle: constants.CHILD_TITLE,
            infantSeatTitle: constants.INFANT_SEAT_TITLE,
            infantLapTitle: constants.INFANT_LAP_TITLE,
            addTravelers: onReviewStep
              ? TRAVELER_SELECT_DROPDOWN_TITLE
              : constants.ADD_TRAVELER_INFO_TEXT,

            completeTravelerProfileTitle: constants.COMPLETE_PROFILE_TITLE,
            completeTravelerProfileSubtitle:
              constants.COMPLETE_PROFILE_SUBTITLE,
            editTravelerTitle: constants.EDIT_TRAVELER_TITLE,
            editTravelerSubtitle: constants.EDIT_TRAVELER_SUBTITLE,
          }}
          travelers={travelers}
          wrapButton={!onReviewStep}
          onClickAddNewTraveler={() => {
            trackEvent({
              eventName: CLICKED_ADD_TRAVELER,
              properties: addTrackingProperties(expState.trackingProperties, {
                entry_type: "checkout",
              } as ClickedAddTravelerProperties),
            });
          }}
          tenant={config.TENANT}
          trackEvent={trackEvent}
          checkMissingInfoCompletion={isCorpTenant(config.TENANT)}
          scrollToDriverLicense={isMissingLicense}
          customHeader={
            <Box className="mobile-car-book-passenger-selection-title-container">
              <Typography component="h1" className="title">
                {TRAVELER_SELECT_DROPDOWN_TITLE}
              </Typography>
              <Typography variant="body2" className="subtitle">
                {TRAVELER_SELECT_DROPDOWN_DESCRIPTION}
              </Typography>
            </Box>
          }
          hideMobileContinueButton={
            !selectedTravelerIds || selectedTravelerIds.length === 0 || isMissingLicense
          }
          useEmbeddedTravelerForm={useEmbeddedTravelerForm}
          isMissingLicense={isMissingLicense}
          userRoles={userRoles}
        />
      )}
    </div>
  );
};
